import { isFloorKind, isTurretKind, UTILITY, WORLD, ZOM, type BuildingKind, type VentDir } from '../shared/defs.ts';
import type { BuildingView, PlayerView, RunView, Snapshot, ZombieView } from '../shared/protocol.ts';
import { cellRect, coreRectAt, costOf, levelOf, maxLevelOf, reachAt, salvageBonusOf } from '../shared/sim/build.ts';
import { clock } from './derive.ts';
import { HIT_FLASH_MS } from './effects.ts';
import { INK, PALETTE, tint } from './palette.ts';
import { drawFallenSoldier } from './bodies.ts';
import { cosLook } from './cosmeticlook.ts';
import { CORE_GLOW, drawCoreBody } from './coreart.ts';
import { coreFlash, onStrike, siege } from './siegefx.ts';
import { animateZombies, biteTarget, cellId, drawHorde } from './zombieart.ts';
import type { Effect } from './state.ts';
import { buildingSolid, drawSolids, LIGHT, standsUp } from './tilt.ts';
import type { Ghost } from './zombies.ts';
import type { Rect } from '../shared/sim/movement.ts';
import { bakedSprite, char, dent, drawEmbers, drawPips, drawTurret, drawTurretLit, lampStutters, seeded, streaks, wearStage, type Wear } from './turretart.ts';
import { drawDecoy, drawDecoyLamp, drawDecoyPull, drawVentFlame, drawVentHead, drawVentJetPreview, drawVentScorch, drawZombieFx } from './ventart.ts';
import { drawRangeRings, rangeRings } from './turretrange.ts';

const TAU = Math.PI * 2;

/** What stands on a raised pad: walls (as their tier), salvage yards, medic posts and decoys. A turret is an emplacement sandbagged straight onto the floor (turretart.ts), so it is no block. */
export const onPad = (b: BuildingView): boolean => standsUp(b) && !isTurretKind(b.kind);
const R = WORLD.playerRadius;

export const CORE_ALERT_MS = 1500;

/** When the core was last bitten, for the alert. Only the night can bite it, so dawn and the report clear the alert at once. */
export function nextCoreHitAt(prev: RunView | null | undefined, run: RunView | null | undefined, now: number, hitAt: number): number {
  if (run?.phase !== 'night') return -Infinity;
  return prev && run.core.hp < prev.core.hp ? now : hitAt;
}

/** Damage numbers and impact effects name walls by their center, since a wall's view carries no id. */
const cellKey = (x: number, y: number) => `${Math.floor(x / ZOM.cell)},${Math.floor(y / ZOM.cell)}`;

export function wallFlashes(effects: readonly Effect[], now: number): Map<string, number> {
  const out = new Map<string, number>();
  for (const fx of effects) if (fx.kind === 'impact' && fx.surface === 'building' && now - fx.born < HIT_FLASH_MS) out.set(cellKey(fx.x, fx.y), fx.born);
  return out;
}

/** `to` is the angle of the turret's last shot (fired at `firedAt`), and `drawn` eases toward it, last eased at `at`. */
export type TurretAim = { to: number; drawn: number; at: number; firedAt: number };

/** A turret turns only to fire, so each shot's angle is its aim until the next; aims of turrets gone from the snapshot are dropped. */
export function aimTurrets(aims: Map<string, TurretAim>, snap: Snapshot, now: number) {
  for (const ev of snap.events) {
    // A coil never turns: its discharge only stamps when it fired, for its crown of sparks.
    if (ev.e === 'coil') { const key = cellKey(ev.x, ev.y), aim = aims.get(key); if (aim) aim.firedAt = now; else aims.set(key, { to: 0, drawn: 0, at: now, firedAt: now }); continue; }
    if (ev.e !== 'turret') continue;
    const key = cellKey(ev.x, ev.y), aim = aims.get(key);
    if (aim) { aim.to = ev.angle; aim.firedAt = now; } else aims.set(key, { to: ev.angle, drawn: ev.angle, at: now, firedAt: now });
  }
  if (!snap.buildings) return;
  const standing = new Set(snap.buildings.map((b) => `${b.cx},${b.cy}`));
  for (const key of aims.keys()) if (!standing.has(key)) aims.delete(key);
}

const TURN_PER_SEC = 14;

const awayFromCore = (b: Pick<BuildingView, 'cx' | 'cy'>, core: { x: number; y: number }) => Math.atan2((b.cy + 0.5) * ZOM.cell - core.y, (b.cx + 0.5) * ZOM.cell - core.x);

/** The barrel's drawn angle eases toward its aim, and how long ago it last fired; a turret that never fired faces away from the core. */
function barrelOf(aims: Map<string, TurretAim>, b: BuildingView, core: { x: number; y: number }, now: number): { angle: number; sinceShot: number } {
  const aim = aims.get(`${b.cx},${b.cy}`);
  if (!aim) return { angle: awayFromCore(b, core), sinceShot: Infinity };
  if (b.kind === 'tesla') return { angle: 0, sinceShot: now - aim.firedAt };
  const d = aim.to - aim.drawn;
  aim.drawn += Math.atan2(Math.sin(d), Math.cos(d)) * Math.min(1, ((now - aim.at) / 1000) * TURN_PER_SEC);
  aim.at = now;
  return { angle: aim.drawn, sinceShot: now - aim.firedAt };
}

/** A raised box seen three-quarter: its top face, then a darker front face hanging below it, each ink-edged, with a lit top edge. */
function box3d(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, depth: number, top: string, front: string) {
  ctx.fillStyle = front;
  ctx.fillRect(x, y + h, w, depth);
  ctx.fillStyle = top;
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = tint(top, 0.24);
  ctx.fillRect(x, y, w, 2);
  ctx.fillRect(x, y, 2, h);
  ctx.fillStyle = tint(top, -0.3);
  ctx.fillRect(x, y + h - 2, w, 2);
  ctx.fillRect(x + w - 2, y, 2, h);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.6;
  ctx.strokeRect(x, y, w, h);
  ctx.strokeRect(x, y + h, w, depth);
}

/**
 * A salvage yard: a heap of the horde's leavings (rusted plate, a tyre, a plank, a bent pipe) on the pad, and a little yellow crane at its back corner
 * swinging a round electromagnet on its cable over the heap. Level 2 adds a conveyor belt feeding the heap, level 3 a second, bigger magnet coil and a
 * hazard band on the jib. Badly damaged, the jib sags and the magnet hangs on the heap.
 */
function drawSalvage(ctx: CanvasRenderingContext2D, cx: number, cy: number, lv: number, wear: Wear = 0) {
  // The conveyor first, under the heap: a dark belt with rollers, running in from the front-left corner.
  if (lv >= 2) {
    box3d(ctx, cx - 20, cy + 4, 20, 6, 3, '#3a3f48', '#2a2e35');
    ctx.fillStyle = '#7d8590';
    for (let i = 0; i < 4; i++) ctx.fillRect(cx - 18 + i * 5, cy + 5, 1.6, 4);
  }
  // The heap: overlapping chunks, each ink-edged, the darkest at the back.
  const chunk = (pts: number[], fill: string) => {
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(cx + pts[0]!, cy + pts[1]!);
    for (let i = 2; i < pts.length; i += 2) ctx.lineTo(cx + pts[i]!, cy + pts[i + 1]!);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = INK;
    ctx.lineWidth = 1.4;
    ctx.stroke();
  };
  chunk([-16, 6, -12, -6, -4, -11, 6, -9, 12, 0, 10, 8, -2, 10], '#6b5a48');
  chunk([-12, 4, -8, -3, 0, -5, 2, 3, -6, 7], '#9a5a32');
  chunk([0, -2, 7, -7, 11, -1, 6, 5], '#7b8290');
  // A tyre and a plank on the pile.
  ctx.fillStyle = '#26282d';
  ctx.beginPath();
  ctx.ellipse(cx - 4, cy + 3, 5, 3.4, -0.3, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.2;
  ctx.stroke();
  ctx.fillStyle = '#4c4f56';
  ctx.beginPath();
  ctx.ellipse(cx - 4, cy + 3, 2, 1.3, -0.3, 0, TAU);
  ctx.fill();
  ctx.save();
  ctx.translate(cx + 3, cy + 5);
  ctx.rotate(-0.35);
  ctx.fillStyle = '#b48a55';
  ctx.fillRect(-7, -1.6, 14, 3.2);
  ctx.strokeStyle = INK;
  ctx.strokeRect(-7, -1.6, 14, 3.2);
  ctx.restore();
  // The crane: a mast at the back right, a jib over the heap, the cable and the magnet.
  const mx = cx + 14, my = cy - 2;
  box3d(ctx, mx - 3.5, my - 2, 7, 7, 3, '#d9a520', '#9c7612');
  const sag = wear === 2 ? 7 : 0;
  const tip = { x: cx - 6, y: cy - 15 + sag };
  ctx.lineCap = 'round';
  ctx.strokeStyle = INK;
  ctx.lineWidth = 4.6;
  ctx.beginPath();
  ctx.moveTo(mx, my);
  ctx.lineTo(mx, my - 14);
  ctx.lineTo(tip.x, tip.y);
  ctx.stroke();
  ctx.strokeStyle = '#f5c400';
  ctx.lineWidth = 2.6;
  ctx.stroke();
  if (lv >= 3) {
    ctx.setLineDash([2.2, 2.2]);
    ctx.strokeStyle = INK;
    ctx.lineWidth = 2.6;
    ctx.beginPath();
    ctx.moveTo(mx - 3, my - 14 + (tip.y - my + 14) * 0.15);
    ctx.lineTo(tip.x + 6, tip.y + (my - 14 - tip.y) * 0.25);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  const mag = { x: tip.x, y: wear === 2 ? cy - 2 : tip.y + 9 };
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(tip.x, tip.y);
  ctx.lineTo(mag.x, mag.y - 3);
  ctx.stroke();
  ctx.lineCap = 'butt';
  const r = lv >= 3 ? 6 : 5;
  ctx.fillStyle = '#3d424c';
  ctx.beginPath();
  ctx.ellipse(mag.x, mag.y, r, r * 0.62, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.4;
  ctx.stroke();
  // The coil's red band and its lit face.
  ctx.strokeStyle = '#d8452b';
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.ellipse(mag.x, mag.y, r - 1.8, (r - 1.8) * 0.62, 0, Math.PI, TAU);
  ctx.stroke();
  ctx.fillStyle = tint('#3d424c', 0.35);
  ctx.fillRect(mag.x - r * 0.5, mag.y - r * 0.45, r, 1.2);
}

/** Where a medic post's lamp sits on its pole, from the cell centre. */
const postLamp = (lv: number) => ({ x: 12 + lv, y: -16 - lv });

/**
 * A medic post: a field tent of olive canvas, its ridge pole and guy lines, the door flap pinned open on a white panel with the kit's green cross, and
 * a pole at its corner carrying its lamp (lit live, `drawPostLamp`). Level 2 adds a stretcher by the door, level 3 a second, bigger cross on the roof.
 * Badly damaged, the tent sags at one end and the lamp pole leans.
 */
function drawPost(ctx: CanvasRenderingContext2D, cx: number, cy: number, lv: number, wear: Wear = 0) {
  const w = 26 + 2 * lv, h = 18 + lv, x0 = cx - w / 2 - 1, y0 = cy - h / 2 - 2;
  const sag = wear === 2 ? 4 : 0;
  // The two roof slopes, the near one lit, and the ridge between them.
  ctx.fillStyle = '#5f6b46';
  ctx.beginPath();
  ctx.moveTo(x0, y0 + h * 0.5); ctx.lineTo(x0 + w, y0 + h * 0.5 + sag); ctx.lineTo(x0 + w, y0 + sag * 0.5); ctx.lineTo(x0, y0);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#7a8758';
  ctx.beginPath();
  ctx.moveTo(x0, y0 + h * 0.5); ctx.lineTo(x0 + w, y0 + h * 0.5 + sag); ctx.lineTo(x0 + w, y0 + h + 2); ctx.lineTo(x0, y0 + h + 2);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(x0, y0); ctx.lineTo(x0 + w, y0 + sag * 0.5); ctx.lineTo(x0 + w, y0 + h + 2); ctx.lineTo(x0, y0 + h + 2); ctx.closePath();
  ctx.moveTo(x0, y0 + h * 0.5); ctx.lineTo(x0 + w, y0 + h * 0.5 + sag);
  ctx.stroke();
  // Seams down the near slope.
  ctx.strokeStyle = 'rgba(28, 31, 38, 0.35)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 1; i < 4; i++) { const x = x0 + (w * i) / 4; ctx.moveTo(x, y0 + h * 0.5 + (sag * i) / 4 + 1); ctx.lineTo(x, y0 + h + 1); }
  ctx.stroke();
  // The door panel: white with the green cross, at the front.
  const dx = cx - 6, dy = y0 + h * 0.5 + 2;
  ctx.fillStyle = '#eef2ea';
  ctx.fillRect(dx, dy, 12, 10);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.2;
  ctx.strokeRect(dx, dy, 12, 10);
  ctx.fillStyle = '#2f9e6f';
  ctx.fillRect(dx + 4.6, dy + 1.6, 2.8, 6.8);
  ctx.fillRect(dx + 2.6, dy + 3.6, 6.8, 2.8);
  if (lv >= 3) {
    ctx.fillStyle = '#eef2ea';
    ctx.fillRect(cx - 5, y0 + 2, 10, 7);
    ctx.strokeRect(cx - 5, y0 + 2, 10, 7);
    ctx.fillStyle = '#2f9e6f';
    ctx.fillRect(cx - 1.2, y0 + 3, 2.4, 5);
    ctx.fillRect(cx - 3.4, y0 + 4.3, 6.8, 2.4);
  }
  if (lv >= 2) {
    // A stretcher on the ground by the door: two poles and its canvas.
    ctx.fillStyle = '#c9c2a8';
    ctx.fillRect(x0 - 2, y0 + h + 5, 13, 5);
    ctx.strokeStyle = INK;
    ctx.lineWidth = 1.1;
    ctx.strokeRect(x0 - 2, y0 + h + 5, 13, 5);
    ctx.beginPath();
    ctx.moveTo(x0 - 5, y0 + h + 5); ctx.lineTo(x0 + 14, y0 + h + 5);
    ctx.moveTo(x0 - 5, y0 + h + 10); ctx.lineTo(x0 + 14, y0 + h + 10);
    ctx.stroke();
  }
  // The lamp pole at the corner.
  const at = postLamp(lv);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(cx + at.x, y0 + h + 2);
  ctx.lineTo(cx + at.x + (wear === 2 ? 5 : 0), cy + at.y + 2);
  ctx.stroke();
}

const UTILITY_HALF = 34;
/** A medic post's mint: its lamp, its pulse and a revive it is making. */
const MEDIC = '#8ff0c4';

/**
 * A salvage yard or medic post baked once per level and state, like the turrets: worn, soot-streaked, dented and pocked; badly damaged,
 * charred black with chunks out of it as well.
 */
function utilitySprite(kind: 'salvage' | 'post', lv: number, wear: Wear, pxPerUnit: number) {
  return bakedSprite(`u|${kind}|${lv}|${wear}`, UTILITY_HALF, pxPerUnit, (g) => {
    const rnd = seeded(lv * 17 + wear * 5 + kind.length);
    if (wear > 0) streaks(g, rnd, wear === 2 ? 9 : 5, 12, wear === 2 ? 32 : 27, wear === 2 ? 3.4 : 2.4, wear === 2 ? 'rgba(18, 15, 13, 0.62)' : 'rgba(24, 20, 17, 0.5)');
    if (kind === 'salvage') drawSalvage(g, 0, 0, lv, wear);
    else drawPost(g, 0, 0, lv, wear);
    if (!wear) return;
    dent(g, -9, -3, 2.6);
    dent(g, 8, -10, 2.2);
    if (wear === 2) dent(g, -2, 4, 2.4);
    g.fillStyle = INK;
    g.beginPath();
    for (let i = 0; i < (wear === 2 ? 9 : 5); i++) { const x = (rnd() - 0.5) * 30, y = (rnd() - 0.5) * 24; g.moveTo(x + 0.95, y); g.arc(x, y, 0.95, 0, TAU); }
    g.fill();
    if (wear === 2) {
      char(g, rnd, 0.42, 5, 20);
      g.fillStyle = INK;
      g.beginPath();
      g.moveTo(-17, 2); g.lineTo(-12, 7); g.lineTo(-17, 8); g.closePath();
      g.moveTo(5, -7); g.lineTo(9, -7); g.lineTo(7, -3.6); g.closePath();
      g.fill();
    }
  });
}

/** A post's lamp, blinking green; a battered one's stutters, and a badly damaged one's is dead. */
function drawPostLamp(ctx: CanvasRenderingContext2D, cx: number, cy: number, lv: number, wear: Wear, now: number) {
  if (wear === 2) return;
  const at = postLamp(lv);
  const on = Math.floor(now / 500) % 2 === 1 && !lampStutters(cx * 3 + cy, now, wear);
  ctx.fillStyle = on ? '#8ff0c4' : '#2f7a58';
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.arc(cx + at.x, cy + at.y, 2.6, 0, TAU);
  ctx.fill();
  ctx.stroke();
}

/** What stands on a cell, drawn over its pad (centred on `cx`, `cy`): a turret's emplacement and gun turned to `angle` (turretart.ts), a coil, a salvage yard's heap and crane, a medic post's tent or a decoy's mast (ventart.ts). */
function drawHead(ctx: CanvasRenderingContext2D, b: { kind: BuildingKind; lv?: number; hp: number; cx?: number; cy?: number; dir?: VentDir; ammo?: number }, cx: number, cy: number, angle: number, sinceShot: number, now: number, pxPerUnit: number) {
  const lv = levelOf(b);
  if (b.kind === 'salvage' || b.kind === 'post') {
    ctx.drawImage(utilitySprite(b.kind, lv, wearStage(b.hp), pxPerUnit), cx - UTILITY_HALF, cy - UTILITY_HALF, UTILITY_HALF * 2, UTILITY_HALF * 2);
    if (b.kind === 'post') drawPostLamp(ctx, cx, cy, lv, wearStage(b.hp), now);
  } else if (b.kind === 'decoy') drawDecoy(ctx, cx, cy, lv, wearStage(b.hp));
  else if (b.kind === 'vent') drawVentHead(ctx, { cx: b.cx ?? 0, cy: b.cy ?? 0, hp: b.hp, lv, dir: b.dir, ammo: b.ammo }, cx, cy, now);
  else if (isTurretKind(b.kind)) drawTurret(ctx, { kind: b.kind, lv, hp: b.hp, x: cx, y: cy, angle, sinceShot }, pxPerUnit);
}

/** A spike strip: a steel rail with a row of spikes standing up from it, ink-edged, lit on one side and shaded on the other, fewer of them as it is trampled. */
export function drawSpikes(ctx: CanvasRenderingContext2D, b: BuildingView, now: number) {
  const { x, y, w, h } = cellRect(b.cx, b.cy);
  const n = Math.max(3, Math.ceil((7 * b.hp) / 10)), left = x + 4, span = w - 8, railY = y + h / 2 + 4;
  void now;
  ctx.fillStyle = 'rgba(20, 22, 28, 0.3)';
  ctx.fillRect(left + LIGHT.x * 5, railY + 3, span, 5);
  ctx.fillStyle = b.hp <= 3 ? '#6a5645' : '#555c67';
  ctx.fillRect(left, railY - 3, span, 6);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.26)';
  ctx.fillRect(left, railY - 3, span, 1.6);
  ctx.fillStyle = 'rgba(10, 12, 16, 0.32)';
  ctx.fillRect(left, railY + 1.6, span, 1.4);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.6;
  ctx.strokeRect(left, railY - 3, span, 6);
  const step = span / n;
  for (let i = 0; i < n; i++) {
    const sx = left + step * (i + 0.5), hgt = 13 - (i % 2) * 2;
    ctx.beginPath();
    ctx.moveTo(sx - 3.4, railY - 3); ctx.lineTo(sx, railY - 3 - hgt); ctx.lineTo(sx + 3.4, railY - 3); ctx.closePath();
    ctx.fillStyle = '#9aa3b0';
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(sx, railY - 3 - hgt); ctx.lineTo(sx + 3.4, railY - 3); ctx.lineTo(sx, railY - 3); ctx.closePath();
    ctx.fillStyle = 'rgba(10, 12, 16, 0.32)';
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(sx - 3.4, railY - 3); ctx.lineTo(sx, railY - 3 - hgt); ctx.lineTo(sx + 3.4, railY - 3); ctx.closePath();
    ctx.stroke();
  }
}

/** Floor items lie under bodies, so they are drawn before the horde and the squad: spike strips, and the scorch down each flame vent's jet (ventart.ts). */
export function drawFloorItems(ctx: CanvasRenderingContext2D, items: readonly BuildingView[], now: number) {
  for (const b of items) {
    if (b.kind === 'spikes') drawSpikes(ctx, b, now);
    else if (b.kind === 'vent') drawVentScorch(ctx, b);
  }
}

export function drawSiegeTops(
  ctx: CanvasRenderingContext2D, buildings: readonly BuildingView[], flashes: ReadonlyMap<string, number>, aims: Map<string, TurretAim>, core: { x: number; y: number }, now: number, pxPerUnit: number,
) {
  for (const b of buildings) {
    const { x, y, w, h } = cellRect(b.cx, b.cy);
    if (b.kind !== 'wall') {
      const barrel = barrelOf(aims, b, core, now);
      drawHead(ctx, b, x + w / 2, y + h / 2, barrel.angle, barrel.sinceShot, now, pxPerUnit);
    }
    const hit = flashes.get(`${b.cx},${b.cy}`);
    if (hit !== undefined) {
      ctx.globalAlpha = 0.7 * (1 - (now - hit) / HIT_FLASH_MS);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x, y, w, h);
      ctx.globalAlpha = 1;
    }
  }
}

/** The sim's cover (turrets.ts aims past walls and crates), as one array that only changes when the walls or crates do, so blind spots are worked out once. */
let cover: { walls: readonly Rect[]; crates: string; rects: Rect[] } | null = null;
function coverOf(walls: readonly Rect[], crates: readonly { x: number; y: number; size: number }[]): Rect[] {
  const key = crates.map((c) => `${c.x},${c.y}`).join(';');
  if (!cover || cover.walls !== walls || cover.crates !== key) cover = { walls, crates: key, rects: [...walls, ...crates.map((c) => ({ x: c.x, y: c.y, w: c.size, h: c.size }))] };
  return cover.rects;
}

/** What the lit pass over the night needs to draw the siege's lights and turret ranges this frame. */
export type SiegeLights = {
  /** Standing buildings in view. */
  buildings: readonly BuildingView[];
  /** Every building, for the rings (a turret's ring reaches into view from off screen). */
  all: readonly BuildingView[];
  aims: Map<string, TurretAim>;
  core: { x: number; y: number };
  day: boolean;
  ghost: Ghost | null;
  cursor: { x: number; y: number } | null;
  upgrade: BuildingView | null;
  /** The night's faint rings round every turret: the Turret ranges setting, once it is dark. */
  squadRings: boolean;
  /** The map's walls and crates: the cover a direct-fire turret cannot see through. */
  walls: readonly Rect[];
  crates: readonly { x: number; y: number; size: number }[];
  now: number;
  pxPerUnit: number;
  scale: number;
  reduced: boolean;
  /** How dark the night is, 0..1. */
  dark: number;
  /** Flame vents in view: their fire is a light. */
  floor: readonly BuildingView[];
  /** The horde in view, for the marks and fire it carries. */
  zombies: readonly ZombieView[];
};

/**
 * The siege's lit layer, drawn over the night's shade: turret ranges (turretrange.ts), then on each turret what it lights
 * (muzzle flash and floor pool, status lamp, ammo gauge, the coil's orb; turretart.ts), then every building's level pips,
 * so a turret reads at a glance in the dark.
 */
export function drawSiegeLights(ctx: CanvasRenderingContext2D, l: SiegeLights) {
  const rings = rangeRings({ ghost: l.ghost, buildings: l.all, day: l.day, cursor: l.cursor, upgrade: l.upgrade, squad: l.squadRings });
  if (rings.length) drawRangeRings(ctx, rings, coverOf(l.walls, l.crates), l.scale, l.now, l.reduced);
  // In build mode each decoy shows how far it draws the horde and each salvage yard the reach its bonus pays in, and one about to go up shows its own.
  if (l.ghost) {
    for (const b of l.all) {
      if (b.kind === 'decoy') drawDecoyPull(ctx, (b.cx + 0.5) * ZOM.cell, (b.cy + 0.5) * ZOM.cell, levelOf(b), l.now, l.scale, l.reduced);
      else if (b.kind === 'salvage') drawSalvageReach(ctx, (b.cx + 0.5) * ZOM.cell, (b.cy + 0.5) * ZOM.cell, levelOf(b), l.now, l.scale, l.reduced);
    }
    const g = l.ghost;
    if (g.kind === 'decoy' && g.refusal !== 'taken' && !g.line) drawDecoyPull(ctx, (g.cx + 0.5) * ZOM.cell, (g.cy + 0.5) * ZOM.cell, 1, l.now, l.scale, l.reduced, true);
    if (g.kind === 'salvage' && g.refusal !== 'taken' && !g.line) drawSalvageReach(ctx, (g.cx + 0.5) * ZOM.cell, (g.cy + 0.5) * ZOM.cell, 1, l.now, l.scale, l.reduced, true);
  }
  for (const b of l.floor) {
    if (b.kind !== 'vent') continue;
    const aim = l.aims.get(`${b.cx},${b.cy}`);
    drawVentFlame(ctx, b, aim ? l.now - aim.firedAt : Infinity, l.now, l.pxPerUnit, l.reduced, l.dark);
  }
  if (l.zombies.length) drawZombieFx(ctx, l.zombies, l.now, l.pxPerUnit, l.reduced);
  for (const b of l.buildings) {
    const { x, y, w, h } = cellRect(b.cx, b.cy);
    // A badly damaged wall, yard or post smoulders (a turret's embers come with its lights).
    if (!('ammo' in b) && wearStage(b.hp) === 2) drawEmbers(ctx, x + w / 2, y + h / 2, l.now, l.pxPerUnit, l.reduced, 1);
    if (b.kind === 'wall') continue;
    if (b.kind === 'decoy') drawDecoyLamp(ctx, b, l.now, l.pxPerUnit, l.reduced, l.dark);
    if ('ammo' in b && b.kind !== 'vent') {
      const aim = l.aims.get(`${b.cx},${b.cy}`);
      const angle = !aim ? awayFromCore(b, l.core) : b.kind === 'tesla' ? 0 : aim.drawn;
      drawTurretLit(ctx, { kind: b.kind, lv: levelOf(b), hp: b.hp, x: x + w / 2, y: y + h / 2, angle, sinceShot: aim ? l.now - aim.firedAt : Infinity, ammo: b.ammo }, l.now, l.pxPerUnit, l.reduced, l.dark);
    }
    drawPips(ctx, levelOf(b), maxLevelOf(b.kind), x, y);
  }
}

/**
 * The reach a salvage yard pays its bonus in, in build mode: a scrap-gold dashed ring at `UTILITY.salvage.reach` at its level, a faint fill, and its
 * bonus written on the ring's top ("+50% scrap"), so a squad sees where its kills will pay more and where two yards overlap (the better one pays).
 * `ghost` draws a yard about to go up a little brighter.
 */
export function drawSalvageReach(ctx: CanvasRenderingContext2D, cx: number, cy: number, lv: number, now: number, scale: number, reduced: boolean, ghost = false) {
  const r = reachAt(UTILITY.salvage.reach, lv), px = 1 / Math.max(0.2, scale);
  ctx.save();
  ctx.fillStyle = `rgba(245, 196, 0, ${ghost ? 0.08 : 0.045})`;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, TAU);
  ctx.fill();
  ctx.lineWidth = 3.4 * px;
  ctx.strokeStyle = 'rgba(28, 31, 38, 0.3)';
  ctx.stroke();
  ctx.setLineDash([10 * px, 7 * px]);
  ctx.lineDashOffset = reduced ? 0 : -now / 80;
  ctx.lineWidth = 1.8 * px;
  ctx.strokeStyle = `rgba(255, 210, 74, ${ghost ? 0.95 : 0.75})`;
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.font = `800 ${Math.round(13 * px)}px "Barlow Condensed", system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 3.2 * px;
  ctx.strokeStyle = 'rgba(28, 31, 38, 0.85)';
  const label = `+${Math.round(salvageBonusOf(lv) * 100)}% scrap`;
  ctx.strokeText(label, cx, cy - r);
  ctx.fillStyle = '#ffd24a';
  ctx.fillText(label, cx, cy - r);
  ctx.restore();
}

/**
 * The Bastion's ground, under everything: a hazard-taped ring painted round it on the floor, the kit's orange on ink, and a
 * cool underglow that breathes with its crystal. The light it gives off is drawn later, over the night (siegefx.ts).
 */
export function drawCoreGlow(ctx: CanvasRenderingContext2D, run: RunView, now: number) {
  const { x, y } = run.core;
  const pulse = 0.5 + 0.5 * Math.sin(now / 420);
  ctx.globalAlpha = 0.05 + 0.04 * pulse;
  ctx.fillStyle = CORE_GLOW;
  ctx.beginPath();
  ctx.arc(x, y, 104, 0, TAU);
  ctx.fill();
  ctx.globalAlpha = 0.55;
  ctx.lineWidth = 8;
  ctx.strokeStyle = INK;
  ctx.beginPath();
  ctx.arc(x, y, 104, 0, TAU);
  ctx.stroke();
  ctx.setLineDash([10, 10]);
  ctx.lineDashOffset = 0;
  ctx.lineWidth = 6;
  ctx.strokeStyle = '#d9541f';
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;
}

export function drawCoreTop(ctx: CanvasRenderingContext2D, run: RunView, now: number, hitAt: number, pxPerUnit = 1) {
  drawCoreBody(ctx, run, now, coreFlash(siege, hitAt, now), pxPerUnit);
}

/**
 * The horde: each zombie's walk and bite advanced from where it is drawn (zombieart.ts), each blow that lands on the core, a
 * building or a squad player bursting sparks and chips off it (siegefx.ts), then the horde drawn batched by kind.
 */
export function drawZombies(ctx: CanvasRenderingContext2D, zombies: readonly ZombieView[], snap: Pick<Snapshot, 'run' | 'buildings' | 'players'>, flashes: ReadonlyMap<number, number>, now: number, pxPerUnit: number) {
  const core = snap.run ? coreRectAt(snap.run.core) : null;
  const buildingAt = new Map((snap.buildings ?? []).filter((b) => !isFloorKind(b.kind)).map((b) => [cellId(b.cx, b.cy), b]));
  const players = snap.players.filter((p) => p.alive).map((p) => ({ x: p.x, y: p.y, r: R }));
  animateZombies(zombies, now, snap.run?.core ?? { x: 0, y: 0 }, (kind, x, y) => biteTarget(kind, x, y, core, buildingAt, players), (st) => onStrike(siege, st, now));
  drawHorde(ctx, zombies, flashes, now, pxPerUnit);
}

/**
 * A downed squadmate: their own soldier lying where they fell, in their colour (they are not dead yet), reaching and
 * struggling; a ring on the floor pulsing red faster as they bleed out; round them the bleed-out draining and, once someone
 * holds use, the revive filling in green; a medic plate bobbing over them; and the time left.
 */
export function drawDowned(ctx: CanvasRenderingContext2D, p: PlayerView, color: string, serverNow: number | null, self: boolean, now = 0, pxPerUnit = 1) {
  const down = p.downed;
  if (!down) return;
  const left = serverNow === null ? ZOM.bleedOutMs : Math.max(0, down.bleedOutAt - serverNow);
  const urgency = 1 - Math.min(1, left / ZOM.bleedOutMs);
  const beat = 0.5 + 0.5 * Math.sin(now / (260 - 150 * urgency));
  // The pulse on the floor, under the body.
  ctx.globalAlpha = (0.18 + 0.22 * beat) * (down.revive > 0 ? 0.4 : 1);
  ctx.fillStyle = down.revive > 0 ? PALETTE.hpGood : PALETTE.hpBad;
  ctx.beginPath();
  ctx.arc(p.x, p.y, R + 8 + 5 * beat, 0, TAU);
  ctx.fill();
  ctx.globalAlpha = 1;
  const struggle = Math.sin(now / 320 + p.id) * 0.25;
  drawFallenSoldier(ctx, color, p.x, p.y, R, { angle: p.angle, splay: [0.5 + struggle, 0.7 - struggle], loll: 0.15 * Math.sin(now / 500 + p.id), scale: 1, helmet: cosLook(p.cos).helmet, camo: cosLook(p.cos).camo }, pxPerUnit);
  // Bleed-out draining, then the revive filling over it.
  const ring = R + 12;
  ctx.lineCap = 'butt';
  ctx.lineWidth = 6;
  ctx.strokeStyle = 'rgba(28, 31, 38, 0.55)';
  ctx.beginPath();
  ctx.arc(p.x, p.y, ring, 0, TAU);
  ctx.stroke();
  ctx.lineWidth = 3.5;
  ctx.strokeStyle = left < 8000 ? PALETTE.hunted : '#ff9f43';
  ctx.beginPath();
  ctx.arc(p.x, p.y, ring, -Math.PI / 2, -Math.PI / 2 + (1 - urgency) * TAU);
  ctx.stroke();
  if (down.revive > 0) {
    ctx.lineWidth = 5;
    ctx.strokeStyle = down.medic ? MEDIC : PALETTE.hpGood;
    ctx.beginPath();
    ctx.arc(p.x, p.y, ring, -Math.PI / 2, -Math.PI / 2 + down.revive * TAU);
    ctx.stroke();
  }
  // A medic post reviving them: a mint dashed ring turning outside the revive, so it reads as the post's work, not a squadmate's.
  if (down.medic) {
    ctx.save();
    ctx.setLineDash([5, 4]);
    ctx.lineDashOffset = -now / 40;
    ctx.lineWidth = 2;
    ctx.strokeStyle = MEDIC;
    ctx.globalAlpha = 0.6 + 0.4 * beat;
    ctx.beginPath();
    ctx.arc(p.x, p.y, ring + 9, 0, TAU);
    ctx.stroke();
    ctx.restore();
  }
  if (self) {
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, ring + 5, 0, TAU);
    ctx.stroke();
  }
  // The medic plate: a white cross on the kit's green, ink-edged, bobbing.
  const mx = p.x, my = p.y - R - 30 + Math.sin(now / 300) * 2;
  ctx.fillStyle = INK;
  ctx.beginPath();
  ctx.arc(mx, my, 9, 0, TAU);
  ctx.fill();
  ctx.fillStyle = down.revive > 0 ? PALETTE.hpGood : tint(PALETTE.hpBad, 0.1 * beat);
  ctx.beginPath();
  ctx.arc(mx, my, 7.5, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(mx - 1.6, my - 4.5, 3.2, 9);
  ctx.fillRect(mx - 4.5, my - 1.6, 9, 3.2);
  if (serverNow !== null) {
    ctx.font = '750 12px "Barlow Condensed", system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 3;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(28, 31, 38, 0.8)';
    ctx.strokeText(clock(left), p.x, p.y + R + 24);
    ctx.fillStyle = left < 8000 ? PALETTE.hunted : '#ffffff';
    ctx.fillText(clock(left), p.x, p.y + R + 24);
    if (down.medic) {
      ctx.strokeText('MEDIC', p.x, p.y + R + 38);
      ctx.fillStyle = MEDIC;
      ctx.fillText('MEDIC', p.x, p.y + R + 38);
    }
  }
}

const GHOST_LOOK = { ok: PALETTE.hpGood, no: PALETTE.hpBad, down: '#ff9f43' } as const;

/** One cell of the ghost: the building seen through where it would stand (unless one stands there), washed and edged in `color`. */
function drawGhostCell(ctx: CanvasRenderingContext2D, ghost: Ghost, cx: number, cy: number, color: string, preview: boolean, core: { x: number; y: number }, now: number, pxPerUnit: number) {
  const { x, y, w, h } = cellRect(cx, cy);
  if (preview) {
    ctx.globalAlpha = 0.6;
    const at = { cx, cy, hp: 10, kind: ghost.kind, lv: ghost.lv, ...(ghost.kind === 'vent' && { dir: ghost.dir ?? 0, ammo: 10 }) } as BuildingView;
    if (ghost.kind === 'spikes') drawSpikes(ctx, at, now);
    else {
      if (onPad(at)) drawSolids(ctx, [buildingSolid(at)]);
      if (ghost.kind !== 'wall') drawHead(ctx, at, x + w / 2, y + h / 2, awayFromCore(at, core), Infinity, now, pxPerUnit);
    }
  }
  ctx.globalAlpha = 0.3 + 0.1 * Math.sin(now / 160);
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);
  ctx.globalAlpha = 1;
  ctx.lineWidth = 3;
  ctx.strokeStyle = color;
  ctx.strokeRect(x + 1.5, y + 1.5, w - 3, h - 3);
}

/** The ghost previews the chosen tier or kind where it would stand, seen through, then a plate naming it (and, over a building, its level, health and upgrade). */
export function drawGhost(ctx: CanvasRenderingContext2D, ghost: Ghost, self: { x: number; y: number }, core: { x: number; y: number }, now: number, pxPerUnit: number) {
  ctx.setLineDash([12, 10]);
  ctx.lineDashOffset = -now / 60;
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(255, 90, 31, 0.7)';
  ctx.beginPath();
  ctx.arc(core.x, core.y, ZOM.buildRadius, 0, TAU);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(40, 44, 52, 0.45)';
  ctx.beginPath();
  ctx.arc(self.x, self.y, ZOM.reachPx, 0, TAU);
  ctx.stroke();
  ctx.setLineDash([]);
  const { x, y, w } = cellRect(ghost.cx, ghost.cy);
  const color = ghost.refusal === null ? GHOST_LOOK.ok : ghost.refusal === 'taken' ? GHOST_LOOK.down : GHOST_LOOK.no;
  if (ghost.line) {
    // A dragged line: every cell its own ghost, green where it goes up and red where it is passed over, each tagged with what it costs.
    const cost = costOf(ghost.kind, ghost.lv);
    for (const c of ghost.line) drawGhostCell(ctx, ghost, c.cx, c.cy, c.refusal === null ? GHOST_LOOK.ok : GHOST_LOOK.no, c.refusal !== 'taken', core, now, pxPerUnit);
    ctx.font = '800 13px "Barlow Condensed", system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 3;
    ctx.lineJoin = 'round';
    for (const c of ghost.line) {
      const at = cellRect(c.cx, c.cy);
      ctx.strokeStyle = 'rgba(28, 31, 38, 0.85)';
      ctx.strokeText(`${cost}`, at.x + at.w / 2, at.y + at.h / 2);
      ctx.fillStyle = c.refusal === null ? '#ffffff' : '#ffb3a8';
      ctx.fillText(`${cost}`, at.x + at.w / 2, at.y + at.h / 2);
    }
  } else {
    // A flame vent about to go up shows the jet it would throw, to aim it down a gap (R turns it).
    if (ghost.kind === 'vent' && ghost.refusal !== 'taken' && ghost.dir !== undefined) drawVentJetPreview(ctx, ghost.cx, ghost.cy, ghost.dir, 1, color, now);
    drawGhostCell(ctx, ghost, ghost.cx, ghost.cy, color, ghost.refusal !== 'taken', core, now, pxPerUnit);
  }
  if (!ghost.label) return;
  const lines = ghost.detail ? [ghost.label, ...ghost.detail.split('\n')] : [ghost.label];
  // The plate is drawn in the world, so zoomed out it grows to stay readable.
  const t = Math.min(1.8, Math.max(1, (globalThis.devicePixelRatio || 1) / ctx.getTransform().a));
  ctx.font = `800 ${14 * t}px "Barlow Condensed", system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const lw = Math.max(...lines.map((l) => ctx.measureText(l).width)) + 16 * t, lh = (12 + lines.length * 14) * t;
  // The interface's plate: gunmetal with its top-right and bottom-left corners clipped.
  const lx = x + w / 2 - lw / 2, ly = y - 10 - lh, c = 6 * t;
  ctx.fillStyle = 'rgba(30, 33, 40, 0.9)';
  ctx.beginPath();
  ctx.moveTo(lx, ly); ctx.lineTo(lx + lw - c, ly); ctx.lineTo(lx + lw, ly + c); ctx.lineTo(lx + lw, ly + lh); ctx.lineTo(lx + c, ly + lh); ctx.lineTo(lx, ly + lh - c);
  ctx.closePath();
  ctx.fill();
  lines.forEach((l, i) => {
    ctx.font = `${i === 0 ? 800 : 600} ${(i === 0 ? 14 : 13) * t}px "Barlow Condensed", system-ui, sans-serif`;
    ctx.fillStyle = i === 0 ? (color === GHOST_LOOK.ok ? '#ffffff' : color) : '#cfc9b8';
    ctx.fillText(l, x + w / 2, ly + (6 + 7 + i * 14) * t);
  });
}
